/*
 * PRD-037 — Sesion del borrador scoped al draft (app SEPARADA del partner).
 *
 * Abre una sesion de comercio sobre el draft (createDraftSession → JWT de comercio) y la coloca
 * en localStorage como TOKEN DE COMERCIO (`token` + `id_commerce`). El interceptor de axiosInstance
 * lo usa para autorizar las llamadas a los endpoints del panel (`/v1/comercio`, `/v1/categoria`, …)
 * sobre ese borrador. Al desmontar, restaura/limpia las llaves para NO contaminar el panel.
 *
 * Es el mecanismo que ya usaba PartnerDraftMenu para reusar el smart menu; se extrae aqui para
 * compartirlo con "Configurar comercio" (una sola fuente de verdad del patron de sesion-draft).
 */
import { useEffect, useRef, useState } from 'react';
import { createDraftSession } from '@services/partnerService';

const KEYS = ['token', 'id_commerce'] as const;

interface DraftSessionState {
  ready: boolean;
  error: string | null;
}

export const useDraftSession = (partnerToken: string | null, idComercio: string | undefined): DraftSessionState => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef<Record<string, string | null>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    // Resetea `ready` al (re)correr el efecto. Sin esto, al navegar entre dos draft distintos en la
    // MISMA ruta el componente NO se remonta: el efecto se re-dispara por el cambio de `idComercio`
    // pero `ready` seguiria en `true` del draft anterior. Ese intervalo es una race: el cleanup ya
    // limpio el token-draft de localStorage y los hijos, creyendo la sesion lista, dispararian
    // requests con token faltante/viejo. Volver a `false` obliga a esperar la nueva sesion-draft.
    // Igual con `error`: un fallo del draft anterior no debe pintar el estado de error del nuevo.
    setReady(false);
    setError(null);
    // Captura los valores previos SINCRONO, antes del await: si el componente se desmonta antes de
    // que createDraftSession resuelva, el cleanup debe restaurar el token/id_commerce reales (mismas
    // llaves compartidas) en vez de borrarlos por un saved.current vacio.
    KEYS.forEach((k) => {
      saved.current[k] = localStorage.getItem(k);
    });
    (async () => {
      if (!partnerToken || !idComercio) {
        setError('Sesion de partner no valida.');
        return;
      }
      try {
        const session = await createDraftSession(partnerToken, idComercio);
        // Si el componente se desmonto durante el await, el cleanup ya restauro/limpio las llaves
        // compartidas (token/id_commerce). Escribir aqui las re-contaminaria con el token del draft
        // DESPUES de navegar fuera (pisando la restauracion del cleanup). Aborta para no contaminar
        // el panel.
        if (!mounted.current) return;
        // Coloca el token de comercio del draft (los valores previos ya quedaron capturados).
        localStorage.setItem('token', session.jwt);
        localStorage.setItem('id_commerce', session.id_comercio);
        setReady(true);
      } catch {
        if (mounted.current) setError('No se pudo abrir la sesion del borrador.');
      }
    })();
    return () => {
      mounted.current = false;
      // Restaura/limpia para NO contaminar el panel con el token del draft.
      KEYS.forEach((k) => {
        const prev = saved.current[k];
        if (prev === null || prev === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, prev);
      });
    };
  }, [partnerToken, idComercio]);

  return { ready, error };
};
